// Bridge: deterministic MoO parser envelope → LLM-shaped envelope.
//
// The existing /api/life/commit-moo-statement endpoint + the existing
// MoO upload preview UI both consume the LLM-shaped envelope from
// /api/life/parse-moo-statement:
//   { statement_meta, rows: [{ producer_number, policy_number,
//     client_name, state, issue_date, paid_to_date, comm_amount,
//     comm_premium, action, plan_code }],
//     producer_summary: { [pn]: { name, row_count, gross,
//                                  chargebacks, net } },
//     totals: { row_count, gross, chargebacks, net,
//               by_action: { new, renewal, chargeback, advance, ... } } }
//
// The deterministic parser at api/_carrierParsers/mutualOfOmaha
// emits a richer carrier-agnostic envelope (separate ledger[] and
// policies[] arrays). This bridge collapses them into the LLM shape
// so we don't need to touch the existing commit + preview surfaces.

// Map MoO activity_type → the existing commit endpoint's `action` enum.
// The existing flow groups actions into:
//   new        — first payment on a brand new issue (positive)
//   renewal    — beyond-first-year payment (BFY = Beyond First Year)
//   chargeback — anything that reverses commission (negatives)
//   advance    — receipt-of-advance payments
//   adjustment — fallback for things that don't fit the above
const ACTIVITY_TO_ACTION = {
  'NEW ISS PYMT':  'new',
  'NEW COV ISS':   'new',
  'NEW ISS REV':   'chargeback',  // reversal of new issue → negative
  'BFY PAYMENT':   'renewal',     // Beyond-First-Year payment
  'BFY REVERSAL':  'chargeback',
  'CANC EFF ISS':  'chargeback',  // cancel effective at issue → claw back
  'LAPSE':         'chargeback',
  'RECPT ADV CO':  'advance',
};

function activityToAction(at, amountSigned) {
  if (!at) {
    // No activity type captured — fall back to sign of the amount.
    return Number(amountSigned) < 0 ? 'chargeback' : 'adjustment';
  }
  const mapped = ACTIVITY_TO_ACTION[at];
  if (mapped) return mapped;
  return Number(amountSigned) < 0 ? 'chargeback' : 'adjustment';
}

// Convert det envelope → LLM envelope. Pure function — easy to test.
export function bridgeDeterministicToLlm(envelope) {
  if (!envelope) return null;
  const meta   = envelope.statement_meta || {};
  const ledger = Array.isArray(envelope.ledger)   ? envelope.ledger   : [];
  const polys  = Array.isArray(envelope.policies) ? envelope.policies : [];
  const prods  = Array.isArray(envelope.producers)? envelope.producers: [];

  // Index policies by policy_number for the join. A policy may have
  // multiple ledger rows (multiple activities in one period); they
  // all share the same policy-level fields (insured, state, etc.).
  const polyByNumber = new Map();
  for (const p of polys) polyByNumber.set(p.policy_number, p);
  const producerName = new Map();
  for (const p of prods) producerName.set(p.producer_number, p.name);

  // Build LLM-shaped rows — one per ledger entry.
  const rows = ledger.map(l => {
    const p = polyByNumber.get(l.policy_number) || {};
    return {
      producer_number: l.writing_agent_id,
      policy_number:   l.policy_number,
      client_name:     p.insured_name || '',
      state:           p.state || '',
      issue_date:      p.issue_date || null,
      paid_to_date:    p.paid_to_date || null,
      comm_amount:     Number(l.amount_signed) || 0,
      comm_premium:    Number(l.commissionable_value) || 0,
      action:          activityToAction(l.activity_type, l.amount_signed),
      plan_code:       p.plan || null,
      // Extras the LLM envelope doesn't carry — pass through so
      // downstream (or future improvements) can use them:
      mga:                p.mga || null,
      writing_agent_name: p.writing_agent_name || producerName.get(l.writing_agent_id) || null,
      comm_rate:          l.comm_rate,
      split_pct:          l.split_pct,
      activity_type_raw:  l.activity_type,
    };
  });

  // Producer rollup — one entry per producer_number with summary stats.
  const summary = {};
  for (const r of rows) {
    const pn = String(r.producer_number || '').trim();
    if (!pn) continue;
    if (!summary[pn]) {
      summary[pn] = {
        name:        producerName.get(pn) || `Producer ${pn}`,
        row_count:   0,
        gross:       0,
        chargebacks: 0,
        net:         0,
      };
    }
    const s = summary[pn];
    s.row_count++;
    if (r.comm_amount > 0) s.gross       += r.comm_amount;
    if (r.comm_amount < 0) s.chargebacks += Math.abs(r.comm_amount);
    s.net += r.comm_amount;
  }

  // Top-level totals.
  const byAction = { new: 0, renewal: 0, chargeback: 0, advance: 0, adjustment: 0 };
  for (const r of rows) byAction[r.action] = (byAction[r.action] || 0) + 1;
  const totals = {
    row_count:   rows.length,
    gross:       rows.filter(r => r.comm_amount > 0).reduce((s, r) => s + r.comm_amount, 0),
    chargebacks: rows.filter(r => r.comm_amount < 0).reduce((s, r) => s + Math.abs(r.comm_amount), 0),
    net:         rows.reduce((s, r) => s + r.comm_amount, 0),
    by_action:   byAction,
  };

  // statement_meta — fill in fields the LLM envelope traditionally has
  // so the preview UI (which reads statement_date / agency_party_id /
  // agency_name) doesn't show blanks.
  const llmMeta = {
    ...meta,
    statement_date:   meta.period_ending || null,        // ISO YYYY-MM-DD
    period_start:     null,                              // not present in det envelope
    period_end:       meta.period_ending || null,
    agency_party_id:  meta.statement_owner_id || null,
    agency_name:      meta.statement_owner_name || null,
  };

  return {
    statement_meta:   llmMeta,
    rows,
    producer_summary: summary,
    totals,
    // Pass through the det-only extras for surfaces that want them:
    _deterministic: {
      checksums:       envelope.checksums || null,
      block_subtotals: envelope.block_subtotals || [],
      warnings:        envelope.warnings || [],
      producers:       prods,
    },
  };
}

// Convert deterministic envelope → the LEGACY upload-flow tx shape.
//
// The legacy CommissionTracker.jsx handleFiles loop expects parseMooPdf's
// return: { transactions: [...], producerNames: {pn: name}, periodEnding }.
// Each tx is read by the merge loop at ~line 3650 with fields:
//   prodNum, policy, insured, state, activityDate, issueDate, planCode,
//   commPremium, activityType, commPct, commAmt, advComm, company, mga
//
// This bridge converts the carrier-agnostic det envelope to that shape
// so the deterministic parser can power the existing upload flow
// (the one bound to the regular "Upload Statement" button — separate
// from the dedicated LifeMooImportPanel surface).
//
// Date normalization: legacy flow uses MM/DD/YYYY string format for
// activityDate. The det envelope emits ISO YYYY-MM-DD. Convert here.
export function bridgeDeterministicToUploadFlow(envelope) {
  if (!envelope) return { transactions: [], producerNames: {}, periodEnding: null };
  const ledger = Array.isArray(envelope.ledger)   ? envelope.ledger   : [];
  const polys  = Array.isArray(envelope.policies) ? envelope.policies : [];
  const prods  = Array.isArray(envelope.producers)? envelope.producers: [];
  const polyByNumber = new Map();
  for (const p of polys) polyByNumber.set(p.policy_number, p);

  const producerNames = {};
  for (const p of prods) {
    if (p.name && !/^Producer\s/i.test(p.name)) producerNames[p.producer_number] = p.name;
  }

  // ISO YYYY-MM-DD → MM/DD/YYYY (the format the legacy monthKey()
  // expects for downstream rollups).
  const isoToMdy = (iso) => {
    if (!iso) return '';
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return iso;
    return `${m[2]}/${m[3]}/${m[1]}`;
  };

  const transactions = ledger.map(l => {
    const p = polyByNumber.get(l.policy_number) || {};
    return {
      prodNum:       l.writing_agent_id,
      policy:        l.policy_number,
      insured:       p.insured_name || '',
      state:         p.state || '',
      activityDate:  isoToMdy(l.period_ending),     // det carries period_ending per ledger row
      issueDate:     isoToMdy(p.issue_date),
      planCode:      p.plan || '',
      commPremium:   Number(l.commissionable_value) || 0,
      activityType:  l.activity_type || '',
      commPct:       Number(l.comm_rate) || 0,
      commAmt:       Number(l.amount_signed) || 0,
      advComm:       0,                              // det envelope doesn't separate; legacy upload merges
      company:       'UNITED',                       // MOO emits "UNITED" in raw rows; legacy normalizer
                                                     // already converts UNITED → "Mutual of Omaha" downstream
      mga:           p.mga || '',
      agentName:     p.writing_agent_name || producerNames[l.writing_agent_id] || '',
    };
  });

  // Period ending — legacy flow uses MM/DD/YYYY too.
  const meta = envelope.statement_meta || {};
  const periodEnding = isoToMdy(meta.period_ending);

  return { transactions, producerNames, periodEnding };
}

// Convert the LLM-vision endpoint's envelope → upload-flow tx shape.
//
// Used when the deterministic parser can't handle a specific EBSSR
// layout (operator reported 2026-06-27: det returned 0 rows for an
// EBSSR PDF that the LLM correctly extracts). The LLM endpoint
// (/api/life/parse-moo-statement) returns rows shaped with snake_case
// field names; the upload-flow merge loop expects camelCase. This
// bridge just renames + light coercions.
export function bridgeLlmToUploadFlow(llmEnv) {
  if (!llmEnv) return { transactions: [], producerNames: {}, periodEnding: null };
  const rows = Array.isArray(llmEnv.rows) ? llmEnv.rows : [];
  const summary = llmEnv.producer_summary || {};

  const producerNames = {};
  for (const [pn, s] of Object.entries(summary)) {
    if (s?.name && !/^Producer\s|^Pending/i.test(s.name)) producerNames[pn] = s.name;
  }
  // Second pass — scan rows for producer_name (added to prompt
  // 2026-06-28). Lets a row's per-row name populate the map even when
  // producer_summary.name came back null on warm-cache responses.
  for (const r of rows) {
    const pn = String(r.producer_number || '').trim();
    const nm = String(r.producer_name || '').trim();
    if (pn && nm && !producerNames[pn] && !/^Producer\s|^Pending/i.test(nm)) {
      producerNames[pn] = nm;
    }
  }

  // The LLM envelope uses ISO dates already; legacy upload-flow
  // monthKey expects MM/DD/YYYY. Same conversion as the det bridge.
  const isoToMdy = (iso) => {
    if (!iso) return '';
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[2]}/${m[3]}/${m[1]}` : String(iso);
  };

  // Pick the BEST dollar amount for this row. The LLM puts the actual
  // $ in different fields depending on the activity:
  //   BFY PAYMENT (renewal) → renewal_amount
  //   NEW ISS PYMT (new sale) → comm_amount
  //   BFY PAYMENT AD (advance) → advance_amount   ← also signed negative for reversals
  //   reversals/lapse        → chargeback_amount
  // The legacy upload merge expects ONE commAmt field, signed.
  // First non-null/non-zero wins.
  function bestAmount(r) {
    const candidates = [
      r.comm_amount, r.commAmt,
      r.advance_amount, r.adv_comm, r.advComm,
      r.renewal_amount,
      r.chargeback_amount,
    ];
    for (const c of candidates) {
      if (c == null) continue;
      const n = Number(c);
      if (Number.isFinite(n) && n !== 0) return n;
    }
    return 0;
  }

  const transactions = rows.map(r => {
    const amount = bestAmount(r);
    return {
      prodNum:      r.producer_number || r.prodNum || '',
      policy:       r.policy_number   || r.policy   || '',
      insured:      r.client_name     || r.insured  || '',
      state:        r.state           || '',
      activityDate: isoToMdy(r.activity_date || r.paid_to_date || r.issue_date || ''),
      issueDate:    isoToMdy(r.issue_date),
      planCode:     r.plan_code || r.plan || '',
      commPremium:  Number(r.comm_premium || r.commPremium || 0),
      // Use raw_action when provided (LLM puts the original carrier
      // string here like "BFY PAYMENT AD") — that's what the legacy
      // merge regexes on for chargeback / activity detection.
      activityType: r.raw_action || r.activity_type_raw || r.activity || r.action || '',
      commPct:      Number(r.comm_rate || r.commPct || 0),
      // Put the actual $ in BOTH commAmt + commPremium-style fields
      // so whichever path the legacy merge takes, it picks the right
      // amount. The merge loop reads commAmt first.
      commAmt:      amount,
      advComm:      Number(r.advance_amount || r.adv_comm || r.advComm || 0),
      company:      r.company || 'UNITED',
      mga:          r.mga || '',
      // Prefer the per-row producer_name from the LLM (added to the
      // prompt schema 2026-06-28 to label override $ by agent name).
      // Fall back to the producer_summary map, then writing_agent_name.
      agentName:    r.producer_name || r.writing_agent_name || producerNames[r.producer_number] || '',
      // Mark advance-action rows so the legacy `t.isAdvance` branch
      // can use advComm directly — matches the Transamerica path.
      isAdvance:    String(r.action || '').toLowerCase() === 'advance' || /\bAD\b/.test(String(r.raw_action || '')),
    };
  });

  const meta = llmEnv.statement_meta || {};
  return {
    transactions,
    producerNames,
    periodEnding: isoToMdy(meta.statement_date || meta.period_end || meta.period_ending),
  };
}

// Convert a pdfjs TextContent page → a single layout-text string.
// Items are sorted by y (top-to-bottom) then x (left-to-right) and
// joined with spaces, with a blank line between distinct y-bands.
//
// Used client-side after `pdfjs.getDocument(...).then(doc => ...)`.
export function pageItemsToLayoutText(items, yBandPx = 4) {
  if (!Array.isArray(items)) return '';
  // pdfjs transform: [a, b, c, d, e, f] where (e, f) is (x, y).
  // pdfjs y origin is bottom-left → flip for top-down sort.
  const decorated = items
    .filter(it => it && typeof it.str === 'string' && it.str.trim().length > 0)
    .map(it => ({
      str: it.str,
      x:   it.transform?.[4] ?? 0,
      y:   it.transform?.[5] ?? 0,
    }));
  decorated.sort((a, b) => (b.y - a.y) || (a.x - b.x));

  // Cluster into y-bands. Two items belong to the same band if their
  // y values are within yBandPx of each other.
  const lines = [];
  let curBand = null;
  for (const it of decorated) {
    if (!curBand || Math.abs(curBand.y - it.y) > yBandPx) {
      curBand = { y: it.y, items: [it] };
      lines.push(curBand);
    } else {
      curBand.items.push(it);
    }
  }
  return lines
    .map(b => b.items.sort((a, b) => a.x - b.x).map(i => i.str).join(' ').replace(/\s+/g, ' ').trim())
    .filter(l => l.length > 0)
    .join('\n');
}
